/**
 * Performance Charts Section
 *
 * Contains performance analytics charts: work packages,
 * value by job type, team performance, value trend
 */

import { clientConfig } from '@/config/clientConfig';
import type { PerformanceAnalyticsResponse } from '@skewb-works/contracts';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';
import { CHART_COLORS } from '../constants/chartConfig';
import { CustomTooltip, SectionTitle } from './ChartComponents';

interface PerformanceChartsSectionProps {
  data: PerformanceAnalyticsResponse;
}

export default function PerformanceChartsSection({ data }: PerformanceChartsSectionProps) {
  // Some clients (e.g. Yorkshire Water) don't surface monetary value on the
  // dashboard — gate the £ value charts behind a client feature flag.
  const showValueCharts = clientConfig.features.dashboardValueCharts;

  return (
    <>
      {/* Value by job type */}
      {showValueCharts && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-card border border-border rounded-lg p-5 shadow-sm">
            <SectionTitle>Total Value (£) by Job Type</SectionTitle>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart
                data={data.totalValueByJobType}
                layout="vertical"
                margin={{ top: 0, right: 16, bottom: 0, left: 72 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  horizontal={false}
                  stroke="var(--border)"
                  strokeOpacity={0.5}
                />
                <XAxis
                  type="number"
                  tick={{ fontSize: 10, fill: 'var(--muted-foreground)' }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v) => `£${(v / 1000).toFixed(0)}k`}
                />
                <YAxis
                  type="category"
                  dataKey="type"
                  tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }}
                  axisLine={false}
                  tickLine={false}
                />
                <CustomTooltip
                  formatter={(v: any) => [`£${Number(v).toLocaleString()}`, 'Value']}
                />
                <Bar
                  dataKey="value"
                  name="£ Value"
                  radius={[0, 4, 4, 0]}
                  fill={CHART_COLORS.orange}
                  maxBarSize={18}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Team performance */}
      <div className="bg-card border border-border rounded-lg p-5 shadow-sm">
        <SectionTitle>Team Member Performance</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart
              data={data.teamPerformance}
              margin={{ top: 4, right: 16, bottom: 56, left: 0 }}
              barGap={4}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" strokeOpacity={0.5} />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 9, fill: 'var(--muted-foreground)' }}
                axisLine={false}
                tickLine={false}
                angle={-20}
                textAnchor="end"
                interval={0}
                dy={8}
              />
              <YAxis
                tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }}
                axisLine={false}
                tickLine={false}
                width={28}
              />
              <CustomTooltip />
              <Legend
                verticalAlign="top"
                align="right"
                iconSize={8}
                wrapperStyle={{ fontSize: 11, paddingBottom: 8 }}
              />
              <Bar
                dataKey="completed"
                name="Completed"
                fill={CHART_COLORS.green}
                radius={[4, 4, 0, 0]}
                maxBarSize={24}
              />
              <Bar
                dataKey="inProgress"
                name="In Progress"
                fill={CHART_COLORS.blue}
                radius={[4, 4, 0, 0]}
                maxBarSize={24}
              />
            </BarChart>
          </ResponsiveContainer>

          <div className="space-y-3">
            {data.teamPerformance.map((m) => (
              <div key={m.name}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-foreground">{m.name}</span>
                  <span
                    className={`text-xs font-semibold tabular-nums ${
                      m.utilisation >= 85
                        ? 'text-orange-600 dark:text-orange-400'
                        : m.utilisation >= 70
                          ? 'text-green-600 dark:text-green-400'
                          : 'text-slate-500'
                    }`}
                  >
                    {m.utilisation}%
                  </span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${
                      m.utilisation >= 85
                        ? 'bg-orange-500'
                        : m.utilisation >= 70
                          ? 'bg-green-500'
                          : 'bg-slate-400'
                    }`}
                    style={{ width: `${m.utilisation}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Total value trend */}
      {showValueCharts && data.valueTrend.length > 0 && (
        <div className="bg-card border border-border rounded-lg p-5 shadow-sm">
          <SectionTitle>Total Job Value (£) — Monthly Trend</SectionTitle>
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={data.valueTrend} margin={{ top: 4, right: 16, bottom: 0, left: 16 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" strokeOpacity={0.5} />
              <XAxis
                dataKey="period"
                tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 10, fill: 'var(--muted-foreground)' }}
                axisLine={false}
                tickLine={false}
                width={52}
                tickFormatter={(v) => `£${(v / 1000).toFixed(0)}k`}
              />
              <CustomTooltip formatter={(v: any) => [`£${Number(v).toLocaleString()}`, 'Value']} />
              <Line
                type="monotone"
                dataKey="value"
                name="£ Value"
                stroke={CHART_COLORS.orange}
                strokeWidth={2.5}
                dot={{ r: 4, fill: CHART_COLORS.orange, stroke: 'white', strokeWidth: 2 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </>
  );
}
